import { useAppSelector } from "@web-solutions/store/types";
import React from "react";
import { useSelector } from "react-redux";

import classNames from "classnames";

import { LString, t, tm, TM } from "@web-solutions/module-localization";
import { LocalizedString } from "@web-solutions/core/constants/remote-config";

import { FitText } from "core/ui-elements";
import { remoteConfigSelector } from "core/store/remote-config/selectors";
import { PriceAfterTrialType } from "core/payment/constants";
import { ProductDetails } from "core/store/billing/selectors";
import TrialsLeft, {
  TrialsLeftType,
} from "core/payment/components/trials-left";

import { DiscountWithTimer } from "../discount-with-timer";
import { SmallBadge } from "../small-badge";
import { SoldLine } from "../sold-line";

import classes from "./style.module.scss";
import { useRemoteConfig } from "@web-solutions/base-app/hooks/use-remote-config";
import getLocale from "core/localization/get-locale";

const tKey = "core.plans";

interface ItemProps {
  isActive: boolean;
  product: ProductDetails;
  onClick: (product: ProductDetails) => void;
  titleText: string;
  subTitleText?: LocalizedString | LString;
  perText: string;
  perTitle?: string;
}

export const Item: React.FC<ItemProps> = ({
  isActive,
  product,
  onClick,
  titleText,
  subTitleText,
  perText,
  perTitle,
}) => {
  const {
    weekPrice,
    priceText,
    period,
    isTrial,
    textLineThrough,
    accentTextLineThrough,
    sold,
    promocode,
    trialsLeftBadge,
    perWeekTitle,
    dayPriceAmount,
    perWeekText,
    title,
    subTitle,
  } = product;

  const locale = getLocale();
  const { priceAfterTrialType } = useSelector(remoteConfigSelector);
  const promocodeActivated = useSelector(
    (state: any) => state.billing.promocodeActivated
  );
  const afterTrialText = tm(
    product.afterTrialText,
    `${tKey}.after_trial.${period}`,
    {
      ...product,
      price:
        priceAfterTrialType === PriceAfterTrialType.WEEK
          ? `${weekPrice}/${t(`core.payment_popup.price_texts.P1W`)}`
          : priceText,
      interpolation: { escapeValue: false },
    }
  );

  const handleItemClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    onClick(product);
  };

  const isSoldOut = !!sold?.total && !sold.left;

  const isLightColorPerWeek = !!accentTextLineThrough && !isActive;

  // react-textfit measures its container on mount and warns "Can not process
  // element without height" when there is nothing to measure — an empty div has
  // no line box, so its height is 0. Both labels below come from the product,
  // which is empty on the first render (before billing/remote config resolve),
  // so resolve the text first and mount Textfit only once there is some.
  const accentLineThroughText = accentTextLineThrough
    ? tm(accentTextLineThrough, "", product)
    : "";
  const perWeekTitleText = dayPriceAmount
    ? tm(perWeekTitle, "", product)
    : "";
  // @ts-ignore
  return (
    <div
      className={classNames(classes.wrap, { [classes.disabled]: isSoldOut })}
    >
      {trialsLeftBadge && <TrialsLeft type={TrialsLeftType.BADGE_PRODUCT} />}
      <DiscountWithTimer
        discount={product.discount}
        // @ts-ignore
        badgeTitle={product.badgeTitle?.[locale]}
        centered={product.badgeCentered}
        disabled={isSoldOut}
      />
      <button
        type="button"
        onClick={handleItemClick}
        className={classNames(classes.container, {
          [classes.isActiveContainer]: isActive,
        })}
        disabled={isSoldOut}
      >
        <div className={classes.detailWrap}>
          <p className={classes.productTitle}>
            {title?.[locale] ?? titleText}

            <SmallBadge texts={product.smallBadges} disabled={isSoldOut} />
          </p>
          {!sold?.hiddenSubTitles ? (
            <>
              <p className={classes.productSubTitle}>
                <TM k={subTitle} defaultKey="" tOptions={product} />
                {/*{subTitle}000*/}
                &nbsp;
                {textLineThrough && (
                  <span className={classes.lineThrough}>
                    {tm(textLineThrough, "", product)}
                  </span>
                )}
              </p>
              {isTrial && (
                <p className={classes.afterTrialText}>
                  {afterTrialText.length > 1
                    ? `Then ${afterTrialText}`
                    : afterTrialText}
                </p>
              )}
            </>
          ) : null}
          {sold?.total ? (
            <SoldLine isActive={isActive} left={sold.left} total={sold.total} />
          ) : null}
        </div>
        <div
          className={classNames(classes.accentWrap, {
            [classes.isActiveAccentWrap]: isActive,
          })}
        >
          {promocodeActivated ? (
            <div className={classes.promo_badge}>
              {t(`${tKey}.promo.discount`, { discount: promocode?.discount })}
            </div>
          ) : accentLineThroughText ? (
            <FitText
              mode="single"
              min={15}
              max={17}
              className={classes.accentTextLineThrough}
            >
              {accentLineThroughText}
            </FitText>
          ) : null}
          {perWeekTitleText ? (
            <FitText
              mode="single"
              min={17}
              max={20}
              className={classNames(
                classes.accentTitle,
                isLightColorPerWeek && classes.lightAccentTitle
              )}
            >
              {/* {dayPriceAmount && perWeekTitle} */}
              <TM
                k={dayPriceAmount && perWeekTitle}
                defaultKey=""
                tOptions={product}
              />
            </FitText>
          ) : null}
          <p
            className={classNames(
              classes.accentSubTitle,
              isLightColorPerWeek && !isActive && classes.lightAccentSubTitle
            )}
          >
            {perWeekText[locale]}
            {/* {title?.[locale] ?? titleText} */}
          </p>
        </div>
      </button>
    </div>
  );
};
